import React, { useEffect, useState } from 'react';
import { FieldProps, FieldValidation } from '@rjsf/core';
import { useGetAPI } from '../../Utils/useGetAPI';
import {
  BackstageIdentityApi,
  microsoftAuthApiRef,
  OAuthApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
  useApi,
} from '@backstage/core-plugin-api';
import {
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Select as MuiSelect,
} from '@material-ui/core';
import axios from 'axios';
import { Select } from '@backstage/core-components';

type prFieldType = {
  conditional: string;
  targetBranch: string;
  targetPath: string;
  repoName: string | null;
  newRepoUrl: string | null;
};

export const PrFields = ({
  onChange,
  rawErrors,
  formData,
  formContext,
}: FieldProps<string>) => {
  const [formState, setFormState] = useState<prFieldType>({
    conditional: '',
    targetBranch: '',
    targetPath: '',
    repoName: '',
    newRepoUrl: '',
  });

  const setFieldState = (key: string, value: string) => {
    const newState = {
      ...formState,
      [key]: value,
    };

    setFormState(newState);
    onChange(newState);
  };

  const [options, setOptions] = useState<
    [
      {
        label: string;
        value: string;
      },
    ]
  >([{ label: 'Loading', value: 'loading' }]);

  const [error, setError] = useState<boolean>(false);

  const url: string = useGetAPI();
  const identityAPI: OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi = useApi(microsoftAuthApiRef);

  const repoUrl: string = formContext.formData.repoUrl || '';

  const repoUrlPrefix: string = `${repoUrl.slice(0, 13)}?`;

  const params: URLSearchParams = new URLSearchParams(repoUrl.slice(14));

  const owner: string = `owner=${params.get('owner')}&`;
  const org: string = `organization=${params.get('organization')}`;

  const getData = async () => {
    const token = await identityAPI.getIdToken();

    await axios
      .get(`${url}/v1/api/devops/repos/${params.get('owner')}`, {
        headers: {
          Authorization: `Basic ${token}`,
        },
      })
      .then(res => {
        const repoArray =
          res !== undefined && res.data.value !== null ? res.data.value : [];

        const scrubRepoArray = Array.from(repoArray)
          .sort()
          .map(repo => {
            return {
              label: repo?.name,
              value: repo?.name,
            };
          });

        scrubRepoArray.unshift({
          label: 'Select a Project',
          value: '',
        });

        setOptions(scrubRepoArray);
        setError(false);
      })
      .catch(e => {
        setError(true);
      });
  };

  useEffect(() => {
    if (owner !== null && owner.length > 1 && org === 'organization=ablcode') {
      setOptions([{ label: 'Loading', value: 'loading' }]);
      getData();
    }

    // only need to listen to when the owner field changes so we can make a single new api call
    // eslint-disable-next-line
  }, [owner]);

  useEffect(() => {
    setFieldState(
      'newRepoUrl',
      `${repoUrlPrefix}${owner}repo=${formState.repoName}&${org}`,
    );
    // only need to listen to when the repoName field changes so we can set url
    // eslint-disable-next-line
  }, [formState.repoName]);

  const conditionalVar = formState.conditional === 'Add files to Existing Repo';

  const conditionalRenderFunc = () => {
    if (formState.conditional === 'Add files to Existing Repo') {
      return (
        <>
          <FormControl margin="normal">
            {error === false ? (
              <Select
                native
                label="Repositories Available"
                onChange={selected => {
                  setFieldState('repoName', selected);
                }}
                items={options}
                selected={formState.repoName}
              />
            ) : (
              <TextField
                id="repoName"
                label="Repository Name"
                variant="standard"
                onChange={e => {
                  setFormState({
                    repoName: e.target.value.toString(),
                    newRepoUrl: `${repoUrlPrefix}${owner}repo=${e.target.value.toString()}&${org}`,
                    conditional: formState.conditional,
                    targetBranch: '',
                    targetPath: '',
                  });
                }}
                value={formState.repoName}
                helperText="Enter the name for the repo."
              />
            )}
          </FormControl>
          <TextField
            id="targetPath"
            label="Target Path"
            variant="standard"
            required={!conditionalVar}
            error={rawErrors?.length > 0 && !formData}
            onChange={e => setFieldState('targetPath', e.target.value)}
            disabled={!conditionalVar}
            value={formState.targetPath || ''}
            helperText="Enter the name of the directory that you want the files to be added to"
          />
          <TextField
            id="targetBranch"
            label="Target Branch"
            variant="standard"
            required={!conditionalVar}
            error={rawErrors?.length > 0 && !formData}
            onChange={e => setFieldState('targetBranch', e.target.value)}
            disabled={!conditionalVar}
            value={formState.targetBranch || ''}
            helperText="Enter the name of the branch that the PR should be opened against"
          />
        </>
      );
    } else if (formState.conditional === 'Create new Repo') {
      return (
        <FormControl margin="normal">
          <TextField
            id="repoName"
            label="Repository Name"
            variant="standard"
            onChange={e => {
              setFormState({
                repoName: e.target.value.toString(),
                newRepoUrl: `${repoUrlPrefix}${owner}repo=${e.target.value.toString()}&${org}`,
                conditional: formState.conditional,
                targetBranch: '',
                targetPath: '',
              });
            }}
            value={formState.repoName}
            helperText="Enter the name for the repo."
          />
        </FormControl>
      );
    }
    return <></>;
  };

  return (
    <FormControl margin="normal">
      <InputLabel id="conditionalFieldLabel">
        Choose if you want to create a new repo or add to an existing repo
      </InputLabel>
      <MuiSelect
        label="conditionalFieldLabel"
        id="conditionalField"
        value={formState.conditional}
        onChange={e => setFieldState('conditional', String(e.target.value))}
      >
        <MenuItem value="Create new Repo">Create new Repo</MenuItem>
        <MenuItem value="Add files to Existing Repo">
          Add files to Existing Repo
        </MenuItem>
      </MuiSelect>
      {conditionalRenderFunc()}
    </FormControl>
  );
};

export const prFieldValidation = (
  value: prFieldType,
  validation: FieldValidation,
) => {
  /** Regex allows any combination of letters, lowercase or uppercase, numbers and special chars \
    Regex excludes any whitespaces */
  const targetPathRegex = /^([A-Za-z0-9\/]*$)(?!.*[[\S])/;

  if (value.conditional === 'Add files to Existing Repo') {
    if (value.targetBranch.length === 0) {
      validation.addError('Target Branch cannot be empty');
    }

    if (value.targetPath.length === 0) {
      validation.addError('Target path cannot be empty');
    } else if (!targetPathRegex.test(value.targetPath)) {
      validation.addError(
        'Target Path can not have whitespaces may only include upper and lowercase letters, numbers and the special char /',
      );
    }
  } else if (
    value.conditional !== 'Add files to Existing Repo' &&
    value.conditional !== 'Create new Repo'
  ) {
    validation.addError(
      'Please choose if you want to create a new repo or add to an existing repo',
    );
  }
};
